/**
 * CarrierDriversPage — /carrier/drivers
 * Carrier admin driver roster with compliance + equipment summary
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { carrierApi, type DriverRosterEntry } from '../lib/api'
import { useAuth } from '../lib/auth-context'

const STATUS_COLORS: Record<string, string> = {
  ACTIVE:      'bg-green-100 text-green-800',
  INACTIVE:    'bg-gray-100 text-gray-600',
  SUSPENDED:   'bg-red-100 text-red-700',
  TERMINATED:  'bg-red-50 text-red-600',
}

function ComplianceCell({ warnings }: { warnings?: string[] }) {
  if (!warnings?.length) return <span className="text-xs text-green-700 font-medium">✓ Clear</span>
  const errors = warnings.filter(w => w.includes('_EXPIRED') || w.includes('FAILED'))
  const warns  = warnings.filter(w => !errors.includes(w))
  return (
    <div className="space-y-0.5">
      {errors.map((w, i) => (
        <div key={i} className="text-xs text-red-700 font-medium">⚠ {w.split(':')[0]}</div>
      ))}
      {warns.slice(0, 2).map((w, i) => (
        <div key={i} className="text-xs text-amber-700">{w.split(':')[0]}</div>
      ))}
      {warns.length > 2 && <div className="text-xs text-gray-400">+{warns.length - 2} more</div>}
    </div>
  )
}

function EquipCell({ entry }: { entry: DriverRosterEntry }) {
  if (!entry.tractorUnit && !entry.trailerUnit) {
    return <span className="text-xs text-gray-400 italic">Not assigned</span>
  }
  return (
    <div className="text-xs space-y-0.5">
      {entry.tractorUnit && (
        <div className="text-gray-700">
          🚛 {entry.tractorUnit.unitNumber ?? entry.tractorUnit.displayName ?? 'Tractor'}
        </div>
      )}
      {entry.trailerUnit && (
        <div className="text-gray-500">
          🚚 {entry.trailerUnit.unitNumber ?? entry.trailerUnit.displayName ?? 'Trailer'}
        </div>
      )}
    </div>
  )
}

export default function CarrierDriversPage() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN', 'DISPATCHER')

  const driversQ = useQuery({
    queryKey: ['carrier-drivers'],
    queryFn: () => carrierApi.listDrivers().then(r => r.data),
    enabled: isAdmin,
  })

  if (!isAdmin) return <div className="p-6 text-red-600 text-sm">Access denied.</div>

  const drivers = driversQ.data?.drivers ?? []
  const warningCount = drivers.filter(d => (d.profile?.complianceWarnings?.length ?? 0) > 0).length
  const noEquipCount = drivers.filter(d => !d.tractorUnit && !d.trailerUnit).length

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Driver Roster</h1>
          {driversQ.data && (
            <p className="text-sm text-gray-500 mt-1">
              {drivers.length} driver{drivers.length !== 1 ? 's' : ''} ·{' '}
              {warningCount > 0 && <span className="text-amber-700">{warningCount} with compliance warnings · </span>}
              {noEquipCount > 0 && <span className="text-gray-500">{noEquipCount} without equipment</span>}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Link to="/carrier/equipment" className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-2 border rounded-lg">
            Fleet Equipment →
          </Link>
        </div>
      </div>

      {driversQ.isLoading && <div className="text-gray-400 text-sm py-8 text-center">Loading drivers…</div>}

      {driversQ.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          Failed to load drivers. {(driversQ.error as any)?.message}
        </div>
      )}

      {!driversQ.isLoading && drivers.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">👤</div>
          <p className="text-sm">No drivers found in your organization.</p>
          <p className="text-xs mt-1">Drivers must sign in at least once before appearing here.</p>
        </div>
      )}

      {drivers.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Driver</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">CDL</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compliance</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Equipment</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {drivers.map(entry => (
                <tr key={entry.auxUserId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {entry.profile?.firstName || entry.profile?.lastName
                        ? `${entry.profile.firstName ?? ''} ${entry.profile.lastName ?? ''}`.trim()
                        : entry.name ?? 'Unnamed Driver'}
                    </div>
                    <div className="text-xs text-gray-400">{entry.email}</div>
                    {entry.profile?.unitLabel && (
                      <div className="text-xs text-blue-700 font-medium">{entry.profile.unitLabel}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {entry.profile?.cdlClass
                      ? <span className="inline-block bg-gray-100 text-gray-800 text-xs font-semibold px-2 py-0.5 rounded">CDL-{entry.profile.cdlClass}</span>
                      : <span className="text-gray-400 text-xs">Not set</span>}
                    {entry.profile?.licenseState && (
                      <span className="ml-1 text-xs text-gray-500">{entry.profile.licenseState}</span>
                    )}
                    {entry.profile?.endorsements && entry.profile.endorsements.length > 0 && (
                      <div className="flex gap-0.5 mt-1">
                        {entry.profile.endorsements.slice(0, 4).map(e => (
                          <span key={e} className="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded">{e}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ComplianceCell warnings={entry.profile?.complianceWarnings} />
                  </td>
                  <td className="px-4 py-3">
                    <EquipCell entry={entry} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${STATUS_COLORS[entry.profile?.driverStatus ?? 'ACTIVE'] ?? 'bg-gray-100 text-gray-600'}`}>
                      {entry.profile?.driverStatus ?? 'ACTIVE'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/carrier/drivers/${entry.auxUserId}`}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                      View / Edit →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4">
        Only accounts with the DRIVER role appear here. Contact AUX admin to add driver accounts.
      </p>
    </div>
  )
}
